import {connect} from "react-redux"
import {createSelector} from "reselect"
import State from "../../../models/State"
import {
    setFacet,
    setFilterClearTypeAction,
    setNumberOfFacetsLoaded,
    setFreeTextFilterSearch,
} from "../../../ducks/search"
import {setViewAllOpen} from "../../../ducks/viewAllModal"
import TrackFilterClear from "../../../events/trackEvent/events/trackFilterClear"
import TrackFilterViewAll from "../../../events/trackEvent/events/trackFilterViewAll"
import {FacetState} from "../../../models/FacetsState"
import {BRAND, FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED_HORIZONTAL} from "../../../config/constants"
import filterThroughBrands from "../../../utils/filterThroughFacets"
import getRealmFromHeaders from "../../../utils/getRealmFromHeaders"
import {shouldDisplayPriceFilter} from "../../../utils/urlBuilder"
import {FilterPriceApiResponse} from "../../../models/Filter"

const selectFacets = (state: State) => state.search.facets
const selectNumberOfFacetsLoaded = (state: State, filterName: string) => state.search.numberOfFacetsLoaded[filterName] // objects used as selectors sometimes don't work
const selectHeaders = (state: State) => state.request.headers
const selectShowRealmInTopFiveBrands = (state: State) => state.search.showRealmInTopFiveBrands
const selectLazyLoadFilterFacetsInHorizontalFilters = (state: State) =>
    state.search.lazyLoadFilterFacetsInHorizontalFilters
const selectFilterName = (state: State, filterName: string) => filterName
const selectFreeTextFiltersList = (state: State) => state.search.freeTextFiltersList
const selectCompositeFiltersList = (state: State) => state.search.compositeFiltersList
const selectFreeTextFilterSearch = (state: State, filterName: string) => state.search.freeTextFilterSearch[filterName]
const selectAlphabticallySortFiltersList = (state: State) => state.search.alphabticallySortedFacetsInHorizontalFilters

export const selectFilteredFacets = createSelector(
    [
        selectFacets,
        selectNumberOfFacetsLoaded,
        selectHeaders,
        selectShowRealmInTopFiveBrands,
        selectLazyLoadFilterFacetsInHorizontalFilters,
        selectFilterName,
        selectFreeTextFiltersList,
        selectCompositeFiltersList,
        selectFreeTextFilterSearch,
        selectAlphabticallySortFiltersList,
    ],
    (
        facets,
        numberOfFacetsLoaded,
        headers,
        showRealmInTopFiveBrands,
        lazyLoadFilterFacetsInHorizontalFilters,
        filterName,
        freeTextFiltersList,
        compositeFiltersList,
        freeTextFilterSearch,
        alphabticallySortFiltersList,
    ) => {
        const facetsAll: FacetState[] = Object.keys(facets)
            .filter(facetName => facetName.startsWith(`${filterName}:`))
            .map(facetName => facets[facetName])

        const numberOfFacetsSelected = facetsAll.filter(facet => facet.s === true).length

        const currentRealm = filterName === BRAND && showRealmInTopFiveBrands ? getRealmFromHeaders(headers) : ""

        const facetsSliceEndIndex = lazyLoadFilterFacetsInHorizontalFilters.includes(filterName)
            ? numberOfFacetsLoaded
            : FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED_HORIZONTAL

        let facetsTruncated = facetsAll.slice(0, facetsSliceEndIndex)
        let popularFacetCount = 0
        if (
            freeTextFiltersList.includes(filterName) ||
            compositeFiltersList.includes(filterName) ||
            alphabticallySortFiltersList.includes(filterName)
        ) {
            const facetNames = facetsAll.map(f => f.v)
            const {filteredFacetsOfTopFive, restOfFilteredFacets} = filterThroughBrands(
                freeTextFilterSearch || "",
                facetNames,
                filterName,
                currentRealm,
            )
            if (alphabticallySortFiltersList.includes(filterName) || compositeFiltersList.includes(filterName)) {
                restOfFilteredFacets.sort((facet1, facet2) => {
                    if (facet1 < facet2) {
                        return -1
                    }
                    if (facet1 > facet2) {
                        return 1
                    }
                    return 0
                })
            }
            const allFacets = [...filteredFacetsOfTopFive, ...restOfFilteredFacets]
            const finalFacets: FacetState[] = []
            allFacets.forEach(brand => {
                const foundFacet = facetsAll.find(f => f.v === brand)
                if (foundFacet) {
                    finalFacets.push(foundFacet)
                }
            })
            popularFacetCount = filteredFacetsOfTopFive.length
            facetsTruncated = finalFacets.slice(0, facetsSliceEndIndex)
        }
        return {popularFacetCount, facets: facetsTruncated, numberOfFacetsSelected, numberOfFacets: facetsAll.length}
    },
)

export const mapStateToProps = (state: State, ownProps: {filterName: string}) => {
    const {
        lazyLoadFilterFacetsInHorizontalFilters,
        alphabticallySortedFacetsInHorizontalFilters,
        showRealmInTopFiveBrands,
        freeTextFiltersList,
        freeTextFilterSearch,
        compositeFiltersList,
    } = state.search
    const {siteUrl} = state.request

    const filterName: string = ownProps.filterName as string

    const filter = state.search.filters[filterName]

    const {numberOfFacetsSelected, facets, popularFacetCount, numberOfFacets} = selectFilteredFacets(state, filterName)

    const anyOptionSelected =
        filterName === "Price" ? shouldDisplayPriceFilter(filter as FilterPriceApiResponse) : numberOfFacetsSelected > 0
    const {text} = state

    const sortFacetsAlphabetically = alphabticallySortedFacetsInHorizontalFilters.includes(filterName)
    const compositeFilter = compositeFiltersList.includes(filterName)
    const lazyLoadFacets = lazyLoadFilterFacetsInHorizontalFilters.includes(filterName)

    return {
        filter,
        allFilters: state.search.filters,
        anyOptionSelected,
        showViewAllButton: lazyLoadFilterFacetsInHorizontalFilters.includes(filterName)
            ? false
            : numberOfFacets > FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED_HORIZONTAL,
        lazyLoadFacets: lazyLoadFacets || sortFacetsAlphabetically || compositeFilter,
        text,
        showBorderAfterTopFive: filterName === BRAND && showRealmInTopFiveBrands,
        siteUrl,
        freeTextFilterSearch,
        freeTextFiltersList,
        sortFacetsAlphabetically,
        compositeFilter,
        numberOfFacetsSelected,
        facets,
        popularFacetCount,
    }
}

export const mapDispatchToProps = (dispatch: any) => ({
    clearTypeFilters: (displayName: string, filterName: string) => {
        TrackFilterClear(displayName)
        dispatch(setFilterClearTypeAction(filterName))
    },
    handleSetFacet: (facetValue: string): void => {
        dispatch(setFacet(facetValue))
    },
    openViewAllModal: (filterName: string) => {
        dispatch(setViewAllOpen(filterName))
        TrackFilterViewAll(filterName)
    },
    updateNumberOfFacetsLoaded: (filterName: string) => {
        dispatch(setNumberOfFacetsLoaded(filterName))
    },
    setFreeTextSearchForFilter: (e: React.SyntheticEvent, filterName: string) => {
        const {value} = e.target as HTMLInputElement
        dispatch(setFreeTextFilterSearch({value, filterName}))
    },
    clearFreeTextSearchForFilter: (filterName: string) => {
        dispatch(setFreeTextFilterSearch({value: "", filterName}))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)
